import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from '@/components/molecules/form-field';
import { GENDERS } from '@/lib/constants/form-options';
import type { CreateProductFormData, UpdateProductFormData } from '../types/product-form.types';

interface ProductAttributesProps<T extends CreateProductFormData | UpdateProductFormData> {
  form: UseFormReturn<T>;
  showGender: boolean;
  showSizes: boolean;
}

export function ProductAttributes<T extends CreateProductFormData | UpdateProductFormData>({
  form,
  showGender,
  showSizes,
}: ProductAttributesProps<T>) {
  const { t } = useTranslation();

  if (!showGender && !showSizes) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('products.attributes')}</CardTitle>
        <CardDescription>{t('products.attributesDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {showGender && (
            <FormField
              control={form.control}
              name={'gender' as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('products.gender')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('products.selectGender')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {GENDERS.map((gender) => (
                        <SelectItem key={gender.value} value={gender.value}>
                          {t(gender.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {showSizes && (
            <FormField
              control={form.control}
              name={'sizes' as any}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('products.sizes')}</FormLabel>
                  <FormControl>
                    <Input placeholder="S, M, L, XL" {...field} />
                  </FormControl>
                  <FormDescription>{t('products.sizesHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
